import { useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Box,
    Checkbox,
} from "@mui/material";
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Check as CheckIcon,
} from "@mui/icons-material";
import MDTypography from '../../../../components/MDTypography';
import MDButton from '../../../../components/MDButton';
function RolePermissions() {
    const [roleEditMode, setRoleEditMode] = useState(false);
    const [selectedRolesToDelete, setSelectedRolesToDelete] = useState([]);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [newRoleName, setNewRoleName] = useState('');

    const [availableRoles, setAvailableRoles] = useState([
        "Admin", "Manager", "User", "Developer", "Analyst"
    ]);

    const permissions = [
        "View Projects",
        "Edit Database",
        "Download Results",
        "Run Calculations",
        "Add Artifacts"
    ];

    const [rolePermissions, setRolePermissions] = useState({
        Admin: {
            "View Projects": true,
            "Edit Database": true,
            "Download Results": true,
            "Run Calculations": true,
            "Add Artifacts": true
        },
        Manager: {
            "View Projects": true,
            "Edit Database": false,
            "Download Results": true,
            "Run Calculations": true,
            "Add Artifacts": true
        },
        User: {
            "View Projects": true,
            "Edit Database": false,
            "Download Results": true,
            "Run Calculations": false,
            "Add Artifacts": false
        },
        Developer: {
            "View Projects": true,
            "Edit Database": true,
            "Download Results": true,
            "Run Calculations": true,
            "Add Artifacts": true
        },
        Analyst: {
            "View Projects": true,
            "Edit Database": false,
            "Download Results": true,
            "Run Calculations": true,
            "Add Artifacts": false
        },
    });

    const handlePermissionChange = (role, permission) => {
        setRolePermissions(prev => ({
            ...prev,
            [role]: {
                ...prev[role],
                [permission]: !prev[role][permission]
            }
        }));
    };

    const handleAddRole = () => {
        if (newRoleName.trim()) {
            const updatedRoles = [...availableRoles, newRoleName];
            setAvailableRoles(updatedRoles);

            setRolePermissions(prev => ({
                ...prev,
                [newRoleName]: {
                    "View Projects": false,
                    "Edit Database": false,
                    "Download Results": false,
                    "Run Calculations": false,
                    "Add Artifacts": false
                }
            }));

            setNewRoleName('');
        }
    };

    const handleDeleteRoles = () => {
        const updatedRoles = availableRoles.filter(role => !selectedRolesToDelete.includes(role));
        setAvailableRoles(updatedRoles);
        
        const updatedPermissions = { ...rolePermissions };
        selectedRolesToDelete.forEach(role => {
            delete updatedPermissions[role];
        });
        setRolePermissions(updatedPermissions);
        
        setSelectedRolesToDelete([]);
        setDeleteConfirmOpen(false);
        setRoleEditMode(false);
    };

    const handleRoleSelectionChange = (role) => {
        setSelectedRolesToDelete(prev => 
            prev.includes(role) 
                ? prev.filter(r => r !== role)
                : [...prev, role]
        );
    };

    return (
        <>
            <TableContainer>
                <Table>
                    <TableHead sx={{ display: "table-header-group"}}>
                        <TableRow>
                            {roleEditMode && (
                                <TableCell sx={{ color: "text.main", width: '48px', padding: '0 8px' }} />
                            )}
                            <TableCell sx={{ color: "text.main", fontWeight: 'bold' }}>Roles</TableCell>
                            {permissions.map((permission) => (
                                <TableCell 
                                    key={permission} 
                                    sx={{ 
                                        color: "text.main", 
                                        fontWeight: 'bold',
                                        textAlign: 'center'
                                    }}
                                >
                                    {permission}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {availableRoles.map((role) => (
                            <TableRow key={role}>
                                {roleEditMode && (
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedRolesToDelete.includes(role)}
                                            onChange={() => handleRoleSelectionChange(role)}
                                        />
                                    </TableCell>
                                )}
                                <TableCell sx={{ fontWeight: 'bold' }}>{role}</TableCell>
                                {permissions.map((permission) => (
                                    <TableCell 
                                        key={`${role}-${permission}`}
                                        align="center"
                                    >
                                        <Checkbox
                                            checked={rolePermissions[role][permission]}
                                            onChange={() => handlePermissionChange(role, permission)}
                                        />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box sx={{ 
                mt: 3, 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box>
                    <MDButton
                        variant="contained"
                        onClick={() => {
                            setRoleEditMode(!roleEditMode);
                            setSelectedRolesToDelete([]);
                        }}
                        startIcon={roleEditMode ? <CheckIcon /> : <EditIcon />}
                        sx={{
                            bgcolor: roleEditMode ? '#4caf50' : "gold.main",
                            color: "navy.main",
                            '&:hover': {
                                bgcolor: roleEditMode ? '#45a049' : "gold.focus",
                            },
                        }}
                    >
                        {roleEditMode ? 'Done' : 'Edit Roles'}
                    </MDButton>
                    {roleEditMode && selectedRolesToDelete.length > 0 && (
                        <MDButton
                            variant="contained"
                            color="error"
                            onClick={() => setDeleteConfirmOpen(true)}
                            startIcon={<DeleteIcon />}
                            sx={{ ml: 2 }}
                        >
                            Delete Selected Roles
                        </MDButton>
                    )}
                </Box>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                }}>
                    <MDTypography variant="h5">Add New Role: </MDTypography>
                    <TextField
                        label="New Role Name"
                        value={newRoleName}
                        onChange={(e) => setNewRoleName(e.target.value)}
                        sx={{
                            '& .MuiInputBase-root': {
                                height: '45px',
                            },
                            width: '200px'
                        }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddRole}
                        disabled={!newRoleName.trim()}
                        sx={{
                            height: '45px',
                            '&:hover': {
                                bgcolor: '#1a3a5f',
                            },
                            '&.Mui-disabled': {
                                bgcolor: 'rgba(43, 85, 135, 0.5)',
                                color: '#FFFFFF'
                            }
                        }}
                    >
                        Add Role
                    </Button>
                </Box>
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
            >
                <DialogTitle>Confirm Role Deletion</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the following roles?
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                        {selectedRolesToDelete.map((role) => (
                            <Typography key={role} sx={{ fontWeight: 'bold' }}>
                                • {role}
                            </Typography>
                        ))}
                    </Box>
                    <Typography sx={{ mt: 2, color: 'error.main' }}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={handleDeleteRoles}
                        variant="contained"
                        color="error"
                    >
                        Delete Roles
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default RolePermissions; 